import React, { Fragment } from "react";
import { DrawerModal } from "@src/components/organisms/modals/drawer-modal/drawer-modal";
import { SmallSpinner } from "@src/components/atoms/small-spinner/small-spinner";
import { useI18N } from "@src/language/language-context";
import styles from "./alarms-notifications-drawer-modal.module.scss";
import {
  SeverityFilterOption,
  useAlarmsNotificationsDrawerModalHook,
} from "./use-alarms-notifications-drawer-modal-hook";
import filterIcon from "@src/assets/svg/machines-filter-icon.svg";
import { Title } from "@src/components/atoms/title/title";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { HorizontalSeparator } from "@src/components/atoms/horizontal-separator/horizontal-separator";
import { AlarmsNotificationCard } from "@src/components/molecules/alarms-notification-card/alarms-notification-card";

const cssClassName = "alarms-notifications-drawer-modal";

export const AlarmsNotificationsDrawerModal = () => {
  const i18n = useI18N();

  const {
    filteredNotifications,
    getSeverityBand,
    getSeverityIcon,
    isLoading,
    openSupportDrawer,
    severityFilter,
    toggleSeverityFilter,
    getFilterCheckboxIconByState,
    isFilterOpen,
    setIsFilterOpen,
    filterToggleRef,
  } = useAlarmsNotificationsDrawerModalHook();

  const texts = {
    title: i18n("modules.alarmsNotificationsDrawerModal.title"),
    severity: i18n("modules.alarmsNotificationsDrawerModal.severity"),
    showAll: i18n("modules.alarmsNotificationsDrawerModal.showAll"),
    urgentSeverity: i18n(
      "modules.alarmsNotificationsDrawerModal.urgentSeverity",
    ),
    warningSeverity: i18n(
      "modules.alarmsNotificationsDrawerModal.warningSeverity",
    ),
    infoSeverity: i18n("modules.alarmsNotificationsDrawerModal.infoSeverity"),
    noNotificationsForSeverity: i18n(
      "modules.alarmsNotificationsDrawerModal.noNotificationsForSeverity",
    ),
    support: i18n("modules.alarmsNotificationsDrawerModal.support"),
    idPrefix: i18n("modules.alarmsNotificationsDrawerModal.idPrefix"),
  };

  const filterOptions: Array<{
    value: SeverityFilterOption;
    label: string;
    icon: string | null;
  }> = [
    { value: "all", label: texts.showAll, icon: null },
    {
      value: "urgent",
      label: texts.urgentSeverity,
      icon: getSeverityIcon("urgent")[0],
    },
    {
      value: "warning",
      label: texts.warningSeverity,
      icon: getSeverityIcon("warning")[0],
    },
    {
      value: "info",
      label: texts.infoSeverity,
      icon: getSeverityIcon("info")[0],
    },
  ];

  return (
    <DrawerModal id="alarmsNotificationsDrawerModal">
      <div className={styles[cssClassName]}>
        <FlexColumnContainer verticalGap={24}>
          <div className={styles[`${cssClassName}__header`]}>
            <Title tag="h2" text={texts.title} />
            <div
              className={styles[`${cssClassName}__filter-toggle`]}
              ref={filterToggleRef}
            >
              <button
                type="button"
                className={styles[`${cssClassName}__filter-btn`]}
                onClick={() => setIsFilterOpen((prev) => !prev)}
              >
                <img
                  src={filterIcon}
                  alt="filter icon"
                  className={styles[`${cssClassName}__filter-icon`]}
                />
                {texts.severity}
              </button>
              {isFilterOpen && (
                <div className={styles[`${cssClassName}__filter-dropdown`]}>
                  {filterOptions.map(({ value, label, icon }) => (
                    <Fragment key={value}>
                      <button
                        className={styles[`${cssClassName}__filter-option`]}
                        onClick={() => {
                          toggleSeverityFilter(value);
                        }}
                      >
                        <span
                          className={
                            styles[`${cssClassName}__filter-option-icon-wrap`]
                          }
                        >
                          {icon && <img src={icon} alt={label} />}
                        </span>
                        <span>{label}</span>
                        <img
                          src={getFilterCheckboxIconByState(
                            severityFilter.includes(value),
                          )}
                          alt=""
                          className={
                            styles[`${cssClassName}__filter-option-check`]
                          }
                        />
                      </button>
                      {value === "all" && (
                        <HorizontalSeparator verticalMargin={0} />
                      )}
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={styles[`${cssClassName}__tickets`]}>
            {isLoading && <SmallSpinner />}

            {filteredNotifications.length === 0 && (
              <p className={styles[`${cssClassName}__status`]}>
                {texts.noNotificationsForSeverity}
              </p>
            )}

            {filteredNotifications.map((notification) => {
              const severityBand = getSeverityBand(notification);
              const severityIcon = getSeverityIcon(severityBand);

              return (
                <AlarmsNotificationCard
                  key={notification.event_id}
                  notification={notification}
                  severityBand={severityBand}
                  severityIcon={severityIcon}
                  idPrefix={texts.idPrefix}
                  supportText={texts.support}
                  onSupportClick={openSupportDrawer}
                />
              );
            })}
          </div>
        </FlexColumnContainer>
      </div>
    </DrawerModal>
  );
};
